import { GLOBAL_FIELD_NAMES, STRENGTH_WEAKNESSES_NAMES } from '../../Sections/Global/form';
import { ol, ul } from '../helpers';

const COMPANY_STRENGTHS_INSTRUCTIONS = {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: {
    name: 'Fortalezas',
    instruction:
      'Identifique las competencias clave y los recursos que proporcionan una ventaja competitiva en el mercado. Destaque los conocimientos especializados, las capacidades distintivas y la reputación de la marca.',
    exampleOne: ul([
      'Experiencia especializada en el diseño y la fabricación de tractores de orugas para suelos pesados o húmedos.',
      'Tecnología patentada de orugas continuas recubiertas de caucho que ofrece una tracción y una flotación superiores.',
      'Un sólido equipo integrado por ingenieros experimentados, especialistas agrícolas y profesionales del sector.',
    ]),
    exampleTwo: ul([
      'Un sistema de pagos seguro garantiza que las transacciones financieras se realicen de forma segura dentro de la plataforma.',
      'Un riguroso proceso de selección de traductores garantiza servicios de alta calidad y fiabilidad.',
      'Equipo especializado con experiencia en programación, servicios lingüísticos y atención al cliente.',
    ]),
  },
};

const COMPANY_WEAKNESSES_INSTRUCTIONS = {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: {
    name: 'Debilidades',
    instruction:
      'Identifique las limitaciones y los desafíos internos de la empresa. Describa con transparencia las áreas que requieren mejoras, como procesos, recursos y capacidades. Analice las brechas con respecto a los estándares del sector, las ineficiencias operativas y su impacto en el rendimiento y la competitividad.',
    exampleOne: ul([
      'La dependencia de una única tecnología patentada para diferenciar los tractores limita la diversificación de productos.',
      'Dificultades para ampliar la capacidad de producción y satisfacer el aumento de la demanda debido a las limitaciones de fabricación.',
      'Escasa diversificación geográfica, con una fuerte dependencia de un mercado o una región específicos para las ventas.',
    ]),
    exampleTwo: ul([
      'Reconocimiento de marca y presencia en el mercado limitados frente a competidores consolidados.',
      'Dependencia de proveedores tecnológicos externos para determinadas funcionalidades de la plataforma.',
      'La dependencia de un equipo relativamente pequeño puede generar limitaciones de recursos durante los períodos de mayor demanda.',
    ]),
  },
};

export const GLOBAL_INSTRUCTIONS = {
  [GLOBAL_FIELD_NAMES.COMPANY_NAME]: {
    name: 'Nombre de la empresa',
    instruction:
      'Indique el nombre completo y oficial previsto para su empresa. Si la empresa aún no está registrada, asegúrese de que el nombre refleje la visión de su marca y la futura denominación oficial.',
    exampleOne: 'Agrotech',
    exampleTwo: 'LanguageLab',
  },

  [GLOBAL_FIELD_NAMES.REGISTRATION_DATE]: {
    name: 'Fecha de registro',
    instruction:
      'Indique la fecha exacta en la que la empresa fue registrada oficialmente, incluyendo el año, el mes y el día.',
    exampleOne: '8.16.2001',
    exampleTwo: '3.15.2008',
  },

  [GLOBAL_FIELD_NAMES.ADDRESS]: {
    name: 'Dirección de registro',
    instruction:
      'Especifique la ciudad y el país donde la empresa está registrada oficialmente. Proporcione información precisa para facilitar consultas legales o transacciones comerciales.',
    exampleOne: '89 N Smeeth St, Charlotte, CA 28202, US',
    exampleTwo: '100 N Tailor St, Charlotte, NC 28202, US',
  },

  [GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION]: {
    name: 'Descripción breve de la empresa',
    instruction:
      'Proporcione una descripción concisa de la empresa. ¿Qué aspectos diferencian su negocio en el mercado? Describa los principales productos o servicios, destacando la innovación, las ventajas competitivas, los hitos más relevantes y las alianzas estratégicas.',
    exampleOne:
      'Agrotech fabrica potentes tractores de orugas para suelos pesados o húmedos. Su exclusivo diseño de oruga continua ofrece una tracción y una flotación superiores, minimizando la presión sobre el suelo gracias a su tecnología patentada de recubrimiento de caucho. La empresa combina alto rendimiento y responsabilidad medioambiental para ofrecer soluciones innovadoras a la agricultura moderna.',
    exampleTwo:
      'LanguageLab es una plataforma de servicios lingüísticos que optimiza la comunicación global en más de 100 idiomas. La empresa permite a clientes y traductores mejorar su experiencia de comunicación mediante procesos de interacción ágiles y eficientes.',
  },

  [GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES]: {
    name: 'Número de empleados',
    instruction:
      'Indique el número actual de empleados de la empresa, incluyendo personal a tiempo completo, a tiempo parcial y trabajadores contratados.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.ANNUAL_INCOME]: {
    name: 'Ingresos anuales históricos',
    instruction:
      'Calcule e indique el ingreso anual promedio sumando los ingresos totales de los últimos tres años y dividiendo el resultado entre tres. Esta cifra debe ser precisa y reflejar de forma consistente el desempeño financiero de la empresa durante ese período.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION]: {
    name: 'Descripción del proyecto',
    instruction:
      'Describa de forma concisa la esencia del proyecto de inversión, ya sea la creación de una nueva instalación, una modernización o una expansión hacia nuevos mercados. Presente brevemente el negocio objetivo, el sector, la posición en el mercado, los principales productos, la situación financiera, los puntos fuertes y la justificación estratégica de la inversión.',
    exampleOne:
      'El plan de negocio se centra en tractores de orugas especializados con tecnología patentada de oruga continua para resolver los problemas de baja productividad en suelos pesados. Gracias a esta innovación, los tractores ofrecen una mayor vida útil, reducen los costes operativos y aumentan la rentabilidad de los agricultores a largo plazo.',
    exampleTwo:
      'La empresa responde a la fragmentación del mercado de la traducción mediante una plataforma centralizada con un sólido proceso de selección de profesionales, pagos seguros y tecnología basada en inteligencia artificial. Nuestra estrategia utiliza la tecnología para optimizar los procesos, mejorando la comunicación, la eficiencia y la confianza entre todas las partes interesadas.',
  },
  [GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES]: {
    name: 'Principales productos o servicios',
    instruction:
      'Presente una lista estructurada de sus productos o servicios. Agrúpelos por categorías o destaque las ofertas principales que definen su negocio. Resalte las características esenciales que diferencian sus productos o servicios de los de la competencia, como la innovación, la calidad o sus atributos únicos.',
    exampleOne:
      'La empresa está especializada en la fabricación de potentes tractores de orugas diseñados para suelos pesados o húmedos. Los productos incluyen:' +
      ol(['Tractores de orugas', 'Equipos agrícolas', 'Soluciones medioambientales']),
    exampleTwo:
      'La empresa ofrece una amplia gama de productos y servicios diseñados para optimizar la comunicación global y mejorar los procesos de interacción. La oferta incluye:' +
      ol([
        'Servicios de traducción',
        'Herramientas de traducción impulsadas por IA',
        'Soluciones para la optimización de la comunicación',
        'Soluciones tecnológicas innovadoras',
      ]),
  },

  [GLOBAL_FIELD_NAMES.COMPANY_SECTOR]: {
    name: 'Sector de actividad',
    instruction:
      'Especifique el sector en el que opera su empresa seleccionando el nivel de detalle adecuado entre las opciones disponibles en la lista desplegable.',
  },

  [GLOBAL_FIELD_NAMES.COMPANY_SUB_SECTOR]: {
    name: 'Subsector de actividad',
    instruction:
      'Especifique el subsector en el que opera su empresa seleccionando el nivel de detalle adecuado entre las opciones disponibles en la lista desplegable.',
  },

  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY_GROUP]: {
    name: 'Grupo industrial',
    instruction:
      'Especifique el grupo industrial al que pertenece su empresa seleccionando el nivel de detalle adecuado entre las opciones disponibles en la lista desplegable.',
  },

  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY]: {
    name: 'Industria',
    instruction:
      'Especifique la industria en la que opera su empresa seleccionando el nivel de detalle adecuado entre las opciones disponibles en la lista desplegable.',
    exampleOne: 'Fabricación de maquinaria y equipos agrícolas.',
    exampleTwo:
      'Servicios de traducción e interpretación.\nPortales de búsqueda en Internet y otros servicios de información.',
  },

  [GLOBAL_FIELD_NAMES.MAIN_GOALS]: {
    name: 'Metas y objetivos principales',
    instruction:
      'Describa los objetivos estratégicos de la empresa, destacando las metas a corto, medio y largo plazo alineadas con su misión y visión. Especifique prioridades como la expansión del mercado, el crecimiento de los ingresos o la innovación de productos. Explique las estrategias, iniciativas e indicadores clave de rendimiento (KPI) que impulsarán el logro de estos objetivos.',
    exampleOne:
      'Largo plazo: alcanzar una posición de liderazgo en el mercado global mediante la diversificación del portafolio de productos, la inversión en investigación y desarrollo de tecnologías agrícolas sostenibles y la expansión hacia mercados emergentes. Los KPI incluyen la cuota de mercado y los niveles de satisfacción del cliente.',
    exampleTwo:
      'Corto plazo: mejorar la usabilidad de la plataforma mediante nuevas funcionalidades y aumentar la base de usuarios a través de campañas de marketing segmentadas. KPI: métricas de participación de los usuarios.',
  },

  [GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS]: {
    name: 'Estrategia para el éxito del proyecto',
    instruction:
      'Defina los pilares estratégicos que impulsarán el éxito del proyecto, como la orientación al cliente, la innovación y la excelencia operativa. Describa las tácticas relacionadas con la diferenciación en el mercado, la innovación, la eficiencia, la interacción con los clientes y la capacidad de adaptación a los desafíos. Destaque la importancia de la mejora continua y la flexibilidad.',
    exampleOne:
      'La estrategia de la empresa para alcanzar el éxito de este proyecto se basa en tres pilares fundamentales: innovación, diferenciación en el mercado y orientación al cliente. La empresa invierte de forma continua en investigación y desarrollo para mejorar su oferta de productos y mantenerse a la vanguardia de las tendencias del mercado.',
    exampleTwo:
      'La estrategia de la empresa se centra en la innovación, la orientación al cliente y la agilidad. Mediante la mejora continua de sus productos basada en la retroalimentación del mercado, la prioridad en la satisfacción del cliente y una rápida adaptación a las tendencias del sector, la empresa busca impulsar un crecimiento sostenible y consolidarse como líder en el mercado de los servicios lingüísticos.',
  },

  [GLOBAL_FIELD_NAMES.TARGET_MARKET]: {
    name: 'Mercado objetivo y consumo',
    instruction:
      'Proporcione una descripción concisa del mercado objetivo, incluyendo sus necesidades, preferencias y principales desafíos. Destaque aspectos diferenciales, como la concentración geográfica. Estime el tamaño del mercado citando fuentes fiables y resalte el valor económico y las oportunidades de negocio.',
    exampleOne:
      'El mercado objetivo de los tractores de orugas incluye diversos actores del sector agrícola, como explotaciones familiares, empresas agrícolas medianas y grandes explotaciones comerciales en distintas regiones geográficas. El consumo anual estimado del mercado objetivo asciende a 100 millones de USD.',
    exampleTwo:
      'El mercado objetivo de la empresa está formado por empresas y particulares con necesidades de comunicación global, incluyendo corporaciones multinacionales, organizaciones internacionales, proveedores de servicios lingüísticos y usuarios que requieren servicios de traducción. El volumen anual del mercado asciende a 49,6 mil millones de USD.',
  },

  [GLOBAL_FIELD_NAMES.TARGET_AUDIENCE]: {
    name: 'Público objetivo',
    instruction:
      'Defina el público objetivo describiendo sus características demográficas, psicográficas y sus necesidades. Explique cómo sus productos o servicios responden a esas necesidades y destaque aspectos diferenciales como la ubicación geográfica, el sector de actividad o los patrones de comportamiento.',
    exampleOne:
      'El público objetivo abarca diversos actores del sector agrícola, entre ellos agricultores, cooperativas agrícolas, empresas agroindustriales y explotaciones rurales. Estas organizaciones constituyen la base de la producción mundial de alimentos y representan diferentes segmentos agrícolas, como cultivos extensivos, cultivos especializados, ganadería y horticultura.',
    exampleTwo:
      'El público objetivo incluye empresas, organizaciones y particulares con necesidades lingüísticas diversas. Los principales tipos de clientes comprenden corporaciones multinacionales, organismos públicos, ONG internacionales, proveedores de servicios lingüísticos, instituciones educativas y personas que requieren servicios de traducción para fines personales o profesionales.',
  },

  [GLOBAL_FIELD_NAMES.TARGET_CHANNELS]: {
    name: 'Canales de venta',
    instruction:
      'Describa los principales canales de distribución, como ventas directas, comercio minorista o comercio electrónico. Explique la lógica de su elección teniendo en cuenta el alcance, las preferencias del mercado y las prácticas del sector. Destaque las ventajas y los desafíos específicos de cada canal.',
    exampleOne: ol([
      'Red de concesionarios y distribuidores: la empresa colabora con concesionarios y distribuidores de todo el mundo para comercializar sus tractores de orugas.',
      'Ventas directas: la empresa cuenta con un equipo comercial propio encargado de atender a los clientes, proporcionar información sobre los productos y gestionar las operaciones de venta.',
    ]),
    exampleTwo:
      'La empresa comercializa sus servicios a través de diversos canales, entre ellos la plataforma en línea, equipos de ventas directas, alianzas con proveedores de servicios lingüísticos, programas de afiliados y la participación en eventos y conferencias del sector. Además, utiliza estrategias de marketing digital como redes sociales y campañas de correo electrónico.',
  },

  [GLOBAL_FIELD_NAMES.MAIN_COMPETITORS]: {
    name: 'Principales competidores',
    instruction:
      'Analice el entorno competitivo identificando los principales competidores, su cuota de mercado, su oferta de productos o servicios y su presencia en el mercado. Destaque sus características distintivas y ventajas competitivas.',
    exampleOne:
      'Los principales competidores de la empresa en el mercado de maquinaria agrícola incluyen gigantes internacionales como Company X y Company Y, así como actores regionales como Company Z. Todos ellos ofrecen tractores y equipos agrícolas con tecnologías avanzadas que compiten directamente con los tractores de orugas de la empresa en términos de rendimiento e innovación.',
    exampleTwo:
      'Los principales competidores de la empresa incluyen proveedores consolidados de servicios lingüísticos como [Competitor A], [Competitor B] y [Competitor C], así como empresas emergentes del sector de la tecnología lingüística como [Competitor D] y [Competitor E]. Estas compañías ofrecen servicios de traducción y soluciones basadas en inteligencia artificial dirigidas al mercado de la comunicación global.',
  },

  [GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME]: {
    name: 'Ingresos anuales proyectados',
    instruction:
      'Calcule el ingreso anual promedio proyectado sumando los ingresos previstos para los próximos tres años y dividiendo el resultado entre tres. Utilice la siguiente fórmula:\n\nIngreso Anual Promedio Proyectado =\n(Ingresos Año 1 + Ingresos Año 2 + Ingresos Año 3) / 3\n\nIndique los ingresos previstos para cada año y justifique sus estimaciones con base en las tendencias del mercado, las previsiones de ventas y otros factores relevantes.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY]: {
    name: 'Rentabilidad proyectada',
    instruction:
      'Para calcular la rentabilidad proyectada en porcentaje:' +
      ol([
        'Estime el beneficio neto futuro.',
        'Proyecte los ingresos totales.',
        'Divida el beneficio neto entre los ingresos totales.',
        'Multiplique el resultado por 100 para convertirlo en un porcentaje.',
      ]) +
      'Utilice la siguiente fórmula: (Beneficio Neto / Ingresos Totales) × 100 = Rentabilidad Proyectada (%).',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD]: {
    name: 'Período de recuperación proyectado',
    instruction:
      'Para calcular el período de recuperación proyectado basado en el beneficio:' +
      ol([
        'Determine la inversión inicial total.',
        'Proyecte el beneficio neto anual.',
        'Divida la inversión inicial total entre el beneficio neto anual.',
        'Convierta el resultado a meses multiplicándolo por 12.',
      ]) +
      'Utilice la siguiente fórmula:\nPeríodo de Recuperación =\n(Inversión Inicial Total / Beneficio Neto Anual) × 12',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.TOTAL_COST]: {
    name: 'Coste total del proyecto',
    instruction:
      'Realice una evaluación exhaustiva de los gastos previstos. El análisis presupuestario debe abarcar todos los aspectos relevantes, incluidos los costes operativos, las inversiones de capital, las iniciativas de marketing, las inversiones tecnológicas y la asignación de recursos humanos.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.FUNDING_AMOUNT]: {
    name: 'Necesidades de financiación',
    instruction:
      'Determine el importe de financiación necesario mediante una evaluación integral de todos los gastos y costes previstos del proyecto.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING]: {
    name: 'Financiación con recursos propios',
    instruction: 'Indique el porcentaje del coste total del proyecto que será financiado con recursos propios.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.MISSION]: {
    name: 'Misión',
    instruction:
      'Defina la misión de la empresa expresando su propósito y sus valores fundamentales. Destaque el impacto que busca generar en el mercado o en la sociedad, resaltando su propuesta de valor única. Alinee la misión con los objetivos estratégicos y refleje el compromiso con principios como la innovación, la orientación al cliente o la sostenibilidad.',
    exampleOne:
      'La misión de la empresa es desarrollar soluciones avanzadas de tractores basadas en la sostenibilidad y la innovación tecnológica al servicio de la comunidad agrícola mundial.',
    exampleTwo:
      'La misión de la empresa es eliminar las barreras lingüísticas y fomentar la conexión global mediante servicios de traducción de alta calidad respaldados por tecnología innovadora y un servicio al cliente excepcional.',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS]: COMPANY_STRENGTHS_INSTRUCTIONS,

  [GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES]: COMPANY_WEAKNESSES_INSTRUCTIONS,

  [GLOBAL_FIELD_NAMES.COMPETITIVE_ADVANTAGE]: {
    name: 'Características distintivas o ventaja competitiva',
    instruction:
      'Comience con una frase impactante que capte la atención del lector. Explique claramente el propósito o la función principal del producto. Destaque una o dos características clave que lo diferencien de las alternativas disponibles en el mercado.',
    exampleOne:
      'Los tractores de orugas destacan por su innovador diseño de oruga continua, que los diferencia de los tractores convencionales de ruedas. Esta tecnología proporciona una tracción y una flotación incomparables, especialmente en suelos pesados o húmedos. Además, el revestimiento de caucho patentado de las orugas reduce la presión sobre el suelo y minimiza los daños en los cultivos y la compactación del terreno.',
    exampleTwo: ol([
      'Tecnología propia: la plataforma utiliza tecnología de inteligencia artificial desarrollada internamente, lo que le proporciona una ventaja competitiva en eficiencia, precisión e innovación.',
      'Amplia cobertura lingüística: con más de 100 idiomas disponibles, los servicios responden a una amplia variedad de necesidades lingüísticas y ofrecen a los clientes un acceso excepcional a la comunicación global.',
    ]),
  },

  [GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS]: {
    name: 'Planes de desarrollo de productos o servicios',
    instruction:
      'Describa la visión estratégica de la organización para el desarrollo y la expansión de sus productos o servicios. Incluya futuras iniciativas de investigación, nuevas líneas de productos y posibles áreas o sectores de crecimiento.',
    exampleOne:
      'Los planes de desarrollo de nuevos productos incluyen:' +
      ol([
        'Implementos y accesorios: la empresa está desarrollando una gama de implementos y accesorios avanzados para tractores de orugas con el fin de aumentar su versatilidad y productividad en tareas agrícolas como la siembra, el laboreo y la cosecha.',
      ]),
    exampleTwo:
      'Posibles áreas de desarrollo de nuevos productos o servicios para la plataforma de servicios lingüísticos:' +
      ol([
        'Servicios de traducción multimodal: desarrollo de soluciones que permitan la traducción de texto, voz, imágenes y vídeo para ofrecer servicios lingüísticos más completos e integrales.',
      ]),
  },

  [GLOBAL_FIELD_NAMES.LOAN_BORROW_AMOUNT]: {
    name: 'Importe previsto del préstamo',
    instruction:
      'Indique el importe del préstamo previsto y explique los motivos para solicitarlo. Justifique el endeudamiento en función de su rentabilidad, las oportunidades de crecimiento y la necesidad de mantener flexibilidad financiera para garantizar el desarrollo sostenible de la empresa.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT]: {
    name: 'Inversión captada',
    instruction:
      'Indique el importe de la inversión prevista y explique la justificación de su necesidad. Fundamente la captación de inversión en las oportunidades de crecimiento, la demanda del mercado y los objetivos estratégicos, destacando cómo contribuirá a incrementar el valor y la competitividad de la empresa.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.REVENUE_STREAMS]: {
    name: 'Modelo de negocio',
    instruction:
      'Describa las principales fuentes de ingresos, como la venta de productos, las suscripciones o la prestación de servicios. Resuma los supuestos financieros clave, incluyendo la estrategia de precios, las tasas de crecimiento y la demanda del mercado. Enumere los socios estratégicos y explique cómo contribuyen al funcionamiento y al crecimiento del negocio.',
    exampleOne:
      'Nuestras principales fuentes de ingresos incluyen la venta directa de productos, los servicios por suscripción y las alianzas estratégicas para desarrollar ofertas conjuntas. Proyectamos el crecimiento de los ingresos basándonos en las tendencias del mercado y en un aumento constante de la captación de clientes. Las hipótesis de costes contemplan estabilidad en los costes de producción y un incremento gradual de la inversión en marketing. Entre nuestros socios estratégicos se encuentran proveedores de materias primas, distribuidores para ampliar el alcance del mercado y empresas tecnológicas que respaldan la plataforma. La colaboración con estos socios fortalece nuestra cadena de valor y amplía nuestra presencia en el mercado.',
    exampleTwo:
      'Nuestras principales fuentes de ingresos incluyen la venta de productos a través de plataformas de comercio electrónico, las licencias de software propietario y los servicios de consultoría adaptados a las necesidades de los clientes. Las previsiones de ingresos se basan en la elasticidad de la demanda del mercado, utilizando estimaciones conservadoras que consideran las variaciones estacionales. Las hipótesis de costes priorizan la eficiencia operativa y la escalabilidad, previendo un crecimiento controlado de los gastos. Las alianzas estratégicas con fabricantes garantizan una cadena de suministro estable, mientras que las agencias de marketing digital fortalecen la visibilidad de la marca.',
  },

  [GLOBAL_FIELD_NAMES.MARKETING_STRATEGY]: {
    name: 'Estrategia de ventas',
    instruction:
      'Describa los principales métodos que utilizará para atraer y fidelizar clientes, como los canales publicitarios, la estrategia de precios y las actividades promocionales. Incluya también su enfoque comercial, detallando los canales de venta, los mercados objetivo y los objetivos de ventas.',
    exampleOne: ol([
      'Red de concesionarios y distribuidores: la empresa colabora con concesionarios y distribuidores de todo el mundo para comercializar sus tractores de orugas.',
      'Ventas directas: la empresa dispone de un equipo comercial propio encargado de atender a los clientes, proporcionar información sobre los productos y gestionar las operaciones de venta.',
    ]),
    exampleTwo:
      'La empresa comercializa sus servicios a través de diversos canales, entre ellos la plataforma en línea, equipos de ventas directas, alianzas con proveedores de servicios lingüísticos, programas de afiliados y la participación en eventos y conferencias del sector. Además, utiliza estrategias de marketing digital, como redes sociales y campañas de correo electrónico.',
  },
};
